import { useEffect, useRef } from 'react';
import { useEditorStore } from '../store/editorStore';
import { getToneAudioEngine } from '../renderer/rendererBridge';

/**
 * Syncs effect state from the editor store to the ToneAudioEngine.
 * Uses zustand subscribe to avoid re-render loops.
 * Mount once in EditorLayout.
 */
export function useEffectsSync() {
  const prevTrackEffectsRef = useRef<Map<string, string>>(new Map());
  const prevEffectClipsRef = useRef<Map<string, string>>(new Map());
  const prevMasterRef = useRef<string>('');

  useEffect(() => {
    const unsub = useEditorStore.subscribe((state) => {
      const engine = getToneAudioEngine();
      if (!engine) return;

      // Register audio tracks with engine (idempotent)
      for (const track of state.project.tracks) {
        if (track.type === 'audio') {
          engine.addTrack(track.id);
        }
      }

      // Sync track effects
      for (const track of state.project.tracks) {
        const effects = track.effects ?? [];
        const key = JSON.stringify(effects);
        if (prevTrackEffectsRef.current.get(track.id) !== key) {
          prevTrackEffectsRef.current.set(track.id, key);
          engine.applyTrackEffects(track.id, effects);
        }
      }

      // Sync effect clips to engine — aggregate ALL effect clips per parent track
      const clipsByParent = new Map<string, typeof state.project.tracks[0]['effectClips']>();
      for (const track of state.project.tracks) {
        if (track.type === 'effect' && track.parentTrackId) {
          const existing = clipsByParent.get(track.parentTrackId) ?? [];
          clipsByParent.set(track.parentTrackId, [...existing, ...(track.effectClips ?? [])]);
        }
      }
      for (const [parentTrackId, allClips] of clipsByParent) {
        const key = JSON.stringify(allClips);
        if (prevEffectClipsRef.current.get(parentTrackId) !== key) {
          prevEffectClipsRef.current.set(parentTrackId, key);
          engine.applyEffectClips(parentTrackId, allClips);
        }
      }

      // Sync master effects
      const masterEffects = state.project.masterEffects ?? [];
      const masterKey = JSON.stringify(masterEffects);
      if (prevMasterRef.current !== masterKey) {
        prevMasterRef.current = masterKey;
        engine.applyMasterEffects(masterEffects);
      }
    });

    return unsub;
  }, []);
}
